"use client";

import { Box, Flex, keyframes, Spinner } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { Text } from "@/components/ui";
import { useHeroVisibility } from "@/lib/contexts";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useTenantData, useTenantLogoUrl } from "@/hooks";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { PublicEntranceScale } from "@/components/shared/motion";

export const AUTH_REDIRECT_DELAY_MS = 1500;

const logoPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.88; transform: scale(1.03); }
`;

const progressFill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const contentFade = keyframes`
  from { opacity: 1; }
  to { opacity: 0.55; }
`;

export interface AuthRedirectViewProps {
  redirectPath: string;
  title: string;
  statusMessage: string;
  redirectingMessage?: string;
  /** When false, rely on layout branding (e.g. admin login card header). */
  showLogo?: boolean;
  redirectDelayMs?: number;
}

function useAuthRedirect(redirectPath: string, redirectDelayMs: number) {
  const router = useRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleRedirect = useCallback(() => {
    setIsRedirecting(true);
    router.push(redirectPath);
  }, [router, redirectPath]);

  useEffect(() => {
    const timer = window.setTimeout(handleRedirect, redirectDelayMs);
    return () => window.clearTimeout(timer);
  }, [handleRedirect, redirectDelayMs]);

  return { isRedirecting };
}

export const AuthRedirectView = ({
  redirectPath,
  title,
  statusMessage,
  redirectingMessage = "Redirecting…",
  showLogo = true,
  redirectDelayMs = AUTH_REDIRECT_DELAY_MS,
}: AuthRedirectViewProps) => {
  const theme = useThemeTokens();
  const { hideHero } = useHeroVisibility();
  const { tenantData } = useTenantData();
  const tenantLogoUrl = useTenantLogoUrl();
  const tenantName = getTenantDisplayName(tenantData);
  const { isRedirecting } = useAuthRedirect(redirectPath, redirectDelayMs);

  useEffect(() => {
    hideHero();
  }, [hideHero]);

  let statusContent: ReactNode;
  if (isRedirecting) {
    statusContent = (
      <Flex align="center" justify="center" gap={2.5}>
        <Spinner size="sm" color={theme.accent.primary} thickness="2px" />
        <Text variant="tertiary" fontSize="sm" lineHeight="1.5">
          {redirectingMessage}
        </Text>
      </Flex>
    );
  } else {
    statusContent = (
      <Text variant="tertiary" fontSize="sm" lineHeight="1.65" textAlign="center">
        {statusMessage}
      </Text>
    );
  }

  return (
    <PublicEntranceScale>
    <Flex
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      gap={5}
      py={{ base: 2, md: 3 }}
      px={1}
      animation={isRedirecting ? `${contentFade} 0.35s ease-out forwards` : undefined}
    >
      {showLogo ? (
        <Box
          w="96px"
          h="96px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          animation={isRedirecting ? undefined : `${logoPulse} 2.4s ease-in-out infinite`}
        >
          <TenantLogo
            width={96}
            height={96}
            tenantData={tenantData}
            src={tenantLogoUrl}
            alt={tenantName}
            priority
            style={{
              filter: theme.mode === "dark" ? "brightness(1.08)" : "none",
            }}
          />
        </Box>
      ) : null}

      <Box w="100%">
        <Text
          as="h2"
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="600"
          letterSpacing="-0.02em"
          color={theme.text.primary}
          lineHeight="1.25"
          mb={2}
        >
          {title}
        </Text>
        {statusContent}
      </Box>

      <Box
        w="100%"
        maxW="220px"
        h="2px"
        borderRadius="full"
        bg={theme.borderGlass.subtle}
        overflow="hidden"
        opacity={isRedirecting ? 0.45 : 1}
        transition="opacity 0.35s ease"
      >
        <Box
          h="100%"
          w="100%"
          borderRadius="full"
          bg={theme.accent.primary}
          transformOrigin="left center"
          animation={
            isRedirecting
              ? undefined
              : `${progressFill} ${redirectDelayMs}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
          }
          sx={isRedirecting ? { transform: "scaleX(1)" } : undefined}
        />
      </Box>
    </Flex>
    </PublicEntranceScale>
  );
};
